import type { FormEvent, FC } from 'react';

import { useApplication } from '@eneco/common/application';
import { getStaticPrefix } from '@eneco/common/env';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import {
  useFinancialsPublicV3CreatePaymentTransactionForInvoiceFromEncryptedQueryStringV3,
  useFinancialsPublicV3CreatePaymentTransactionForPaymentArrangementFromEncryptedQueryStringV3,
} from '@eneco/dc/hooks';
import { Bucket, Button, Form, Heading, Stack, Image } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { IDealPaymentV2Rendering } from '@sitecore/types/IDealPaymentV2';

const IDEAL_LOGO_SIZE = '32px';

export const IDealPaymentForm: FC = () => {
  const { f } = useContent<IDealPaymentV2Rendering>();
  const { searchParams } = useApplication();

  const invoiceType = (searchParams.get('type') || 'nota') as 'nota' | 'betaaltermijn';

  const usePaymentTransactionForInvoice =
    useFinancialsPublicV3CreatePaymentTransactionForInvoiceFromEncryptedQueryStringV3();
  const usePaymentTransactionForPaymentArrangement =
    useFinancialsPublicV3CreatePaymentTransactionForPaymentArrangementFromEncryptedQueryStringV3();

  const {
    send,
    isLoading,
    isError: isCreatePaymentLinkError,
  } = invoiceType === 'nota' ? usePaymentTransactionForInvoice : usePaymentTransactionForPaymentArrangement;

  const query = searchParams.get('query') || searchParams.get('id');
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const response = await send({
      query,
      merchantReturnUrl: `${window.location.href.split('?')[0]}?query=${encodeURIComponent(query ?? '')}`,
    });

    if (response?.redirectUrl) {
      window.location.assign(response.redirectUrl);
    }
  };

  const iDealLogoURL = `${getStaticPrefix()}${'/images/multilabel/ideal-wero.png'}`;

  return (
    <Form onSubmit={event => handleSubmit(event)}>
      <Bucket title={f('cardTitle.value')}>
        <Bucket.Content>
          <Stack gap="6">
            <Stack gap="2" direction="row" alignY="center">
              <Image src={iDealLogoURL} alt="" width={IDEAL_LOGO_SIZE} />
              <Heading as="h1" size="XS">
                {f('generalTitle.value')}
              </Heading>
            </Stack>
          </Stack>
          <Bucket.Actions>
            <Button isLoading={isLoading} type="submit">
              {f('payButtonLabel.value')}
            </Button>
          </Bucket.Actions>
          {isCreatePaymentLinkError && (
            <Stack.Item>
              <TrackedNotificationBox
                variant="error"
                label={f('notificationErrorField.value.title') as string}
                text={<RichText html={f('notificationErrorField.value.content') as string} />}
                isAlert={true}
              />
            </Stack.Item>
          )}
        </Bucket.Content>
      </Bucket>
    </Form>
  );
};
